import * as React from "react";
import gql from "graphql-tag";
import debounce from "lodash/debounce";
// tslint:disable-next-line:no-submodule-imports
import AsyncSelect from "react-select/lib/Async";
import { useAdminClient } from "../App/ClientProvider";
import { SearchClients, SearchClientsVariables } from "../admin-gql";

const selectClientsListGql = gql`
  query SelectClientsList($filter: String) {
    searchClients(filter: $filter, first: 20) {
      edges {
        node {
          clientId
          displayName
        }
      }
    }
  }
`;

type ClientOption = { label: string; value: string };

export const SelectClients = (props: {
  selectedClientIds: string[];
  onChange: (clientIds: string[]) => void;
  disabled?: boolean;
}) => {
  const client = useAdminClient();
  // Cache of clientId -> displayName for already-selected clients
  const [labelCache, setLabelCache] = React.useState<Record<string, string>>({});

  // Resolve display names for selected IDs not yet in cache
  React.useEffect(() => {
    const missing = props.selectedClientIds.filter(id => !labelCache[id]);
    if (missing.length === 0) return;

    missing.forEach(id => {
      client
        .query<SearchClients, SearchClientsVariables>({
          query: selectClientsListGql,
          variables: { filter: id }
        })
        .then(result => {
          const edges = (result.data && result.data.searchClients && result.data.searchClients.edges) || [];
          const match = edges.find(e => e.node.clientId === id);
          if (match) {
            setLabelCache(prev => ({ ...prev, [id]: match.node.displayName || id }));
          }
        })
        .catch(() => {/* leave cache entry absent — will fall back to raw id */});
    });
  }, [props.selectedClientIds]); // eslint-disable-line react-hooks/exhaustive-deps

  const debouncedSearch = React.useRef(
    debounce((inputValue: string, callback: (options: ClientOption[]) => void) => {
      client
        .query<SearchClients, SearchClientsVariables>({
          query: selectClientsListGql,
          variables: { filter: inputValue }
        })
        .then(result => {
          const edges = (result.data && result.data.searchClients && result.data.searchClients.edges) || [];
          const options = edges.map(x => ({
            label: x.node.displayName || x.node.clientId,
            value: x.node.clientId
          }));
          // Warm the label cache with search results so newly-selected items show display names immediately
          setLabelCache(prev => {
            const next = { ...prev };
            options.forEach(o => { next[o.value] = o.label; });
            return next;
          });
          callback(options);
        })
        .catch(() => callback([]));
    }, 300)
  );

  React.useEffect(() => {
    return () => debouncedSearch.current.cancel();
  }, []);

  const loadOptions = (inputValue: string, callback: (options: ClientOption[]) => void) => {
    if (!inputValue) {
      callback([]);
      return;
    }
    debouncedSearch.current(inputValue, callback);
  };

  const value = props.selectedClientIds.map(id => ({
    label: labelCache[id] || id,
    value: id
  }));

  return (
    <AsyncSelect
      isMulti
      isDisabled={props.disabled}
      value={value}
      loadOptions={loadOptions}
      noOptionsMessage={({ inputValue }: { inputValue: string }) => (inputValue ? "No clients found" : "Type to search...")}
      placeholder="Select..."
      onChange={(options: ClientOption[] | null) =>
        props.onChange((options || []).map(x => x.value))
      }
    />
  );
};
